import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { formatDistanceToNow } from "date-fns";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import type { DataAsset, DataAssetType } from "../types";
import { DataGrid } from "./data-grid";

interface DataAssetsTableProps {
  data: DataAsset[];
  isLoading?: boolean;
  onEdit?: (asset: DataAsset, newName: string) => void;
  onDelete?: (asset: DataAsset) => void;
  onView?: (asset: DataAsset) => void;
  enableEdit?: boolean;
  enableDelete?: boolean;
  enableView?: boolean;
  height?: number | string;
}

const dataTypeColors: Record<DataAssetType, string> = {
  raw_text: "#1976d2",
  raw_json: "#2e7d32",
  text: "#0288d1",
  json: "#388e3c",
  csv: "#f57c00",
  excel: "#ff6f00",
  pdf: "#d32f2f",
  image: "#7b1fa2",
  other: "#616161",
};

const dataTypeLabels: Record<DataAssetType, string> = {
  raw_text: "Raw Text",
  raw_json: "Raw JSON",
  text: "Text",
  json: "JSON",
  csv: "CSV",
  excel: "Excel",
  pdf: "PDF",
  image: "Image",
  other: "Other",
};

export const DataAssetsTable: React.FC<DataAssetsTableProps> = ({
  data,
  isLoading = false,
  onEdit,
  onDelete,
  onView,
  enableEdit = true,
  enableDelete = true,
  enableView = true,
  height = 500,
}) => {
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    asset?: DataAsset;
    name: string;
  }>({ open: false, name: "" });

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    asset?: DataAsset;
  }>({ open: false });

  const handleEditClick = useCallback((asset: DataAsset) => {
    setEditDialog({ open: true, asset, name: asset.name });
  }, []);

  const handleEditConfirm = () => {
    if (editDialog.asset && onEdit && editDialog.name.trim()) {
      onEdit(editDialog.asset, editDialog.name.trim());
      setEditDialog({ open: false, name: "" });
    }
  };

  const handleDeleteClick = useCallback((asset: DataAsset) => {
    setDeleteDialog({ open: true, asset });
  }, []);

  const handleDeleteConfirm = () => {
    if (deleteDialog.asset && onDelete) {
      onDelete(deleteDialog.asset);
      setDeleteDialog({ open: false });
    }
  };

  const handleViewClick = useCallback(
    (asset: DataAsset) => {
      if (onView) {
        onView(asset);
      }
    },
    [onView],
  );

  const columns = useMemo<GridColDef<DataAsset>[]>(() => {
    const cols: GridColDef<DataAsset>[] = [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "data_type",
        headerName: "Type",
        width: 120,
        renderCell: (params: GridRenderCellParams<DataAsset>) => (
          <Chip
            label={dataTypeLabels[params.value as DataAssetType]}
            size="small"
            sx={{
              backgroundColor: dataTypeColors[params.value as DataAssetType],
              color: "white",
            }}
          />
        ),
      },
      {
        field: "created_at",
        headerName: "Created",
        width: 200,
        valueGetter: (params) => {
          if (!params) return "-";
          return formatDistanceToNow(new Date(params), { addSuffix: true });
        },
      },
    ];

    if (enableEdit || enableDelete || enableView) {
      cols.push({
        field: "actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        renderCell: (params: GridRenderCellParams<DataAsset>) => (
          <Stack direction="row" alignItems="center" justifyContent="center" height="100%">
            {enableView && (
              <Tooltip title="View">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewClick(params.row);
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {enableEdit && (
              <Tooltip title="Edit Name">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {enableDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(params.row);
                  }}
                  sx={{ color: "error.main" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      });
    }

    return cols;
  }, [enableEdit, enableDelete, enableView, handleDeleteClick, handleEditClick, handleViewClick]);

  return (
    <Box>
      <DataGrid
        rows={data}
        columns={columns}
        loading={isLoading}
        customSx={{
          height: height,
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
      />

      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, name: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Data Asset Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={editDialog.name}
            onChange={(e) => setEditDialog((prev) => ({ ...prev, name: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleEditConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditDialog({ open: false, name: "" })}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditConfirm}
            variant="contained"
            disabled={!editDialog.name.trim()}
            size="small"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Data Asset</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{deleteDialog.asset?.name}"? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })} variant="outlined" size="small">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" size="small">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
